import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useOrderEvents } from "@/hooks/use-order-events";
import { useOrders } from "@/hooks/use-orders";
import { usePublicMenu } from "@/hooks/use-menu";
import { formatRelativeFromNow } from "@/lib/format-time";
import { queryKeys } from "@/lib/query-keys";

export default function OrdersKitchenBoard() {
  const { t, i18n } = useTranslation();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const queryClient = useQueryClient();
  const { data: menuData } = usePublicMenu(orgSlug);
  const { data, error } = useOrders({
    status: ["accepted", "in_progress"],
    limit: 100,
  });

  useOrderEvents(orgSlug ? { type: "board", orgSlug } : { type: "org" }, () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() });
  });

  useEffect(() => {
    document.body.classList.add("kitchen-board");
    return () => {
      document.body.classList.remove("kitchen-board");
    };
  }, []);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center text-destructive">
        {error.message}
      </div>
    );
  }

  const orders = data?.orders ?? [];

  return (
    <div className="min-h-screen bg-background p-6">
      <header className="mb-6 flex items-center justify-between">
        <h1 className="font-bold font-serif text-4xl tracking-tight">
          {menuData?.organization.name ?? ""} ·{" "}
          {t("boards.kitchen.title", { defaultValue: "Cocina" })}
        </h1>
        <div className="text-muted-foreground text-sm">
          {t("boards.kitchen.activeOrders", {
            count: orders.length,
            defaultValue: "{{count}} pedidos activos",
          })}
        </div>
      </header>

      {orders.length === 0 ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground text-xl italic">
          {t("boards.kitchen.empty", {
            defaultValue: "No hay pedidos en preparación",
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {orders.map((order) => (
            <article
              className="rounded-2xl border border-border bg-card p-5 shadow-md"
              key={order.id}
            >
              <header className="mb-3 flex items-baseline justify-between">
                <span className="font-bold text-2xl">#{order.dailyNumber}</span>
                <span className="font-mono text-muted-foreground text-sm">
                  {formatRelativeFromNow(order.createdAt, i18n.language)}
                </span>
              </header>
              <ul className="space-y-1.5">
                {order.items.map((item) => (
                  <li className="text-base" key={item.id}>
                    <span className="mr-2 inline-block min-w-[2ch] font-bold tabular-nums">
                      {item.quantity}×
                    </span>
                    {item.nameSnapshot}
                    {item.notes ? (
                      <div className="text-amber-700 text-xs">⚠ {item.notes}</div>
                    ) : null}
                  </li>
                ))}
              </ul>
              {order.notes ? (
                <p className="mt-3 rounded bg-amber-50 px-2 py-1 text-amber-900 text-sm">
                  {order.notes}
                </p>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
